import { Controller } from "@hotwired/stimulus"

// A spreadsheet open for editing (assistant_documents/_grid_editor). The cells are
// the editor: contenteditable, one plain-text value each. Whatever is in the grid
// is the document - every edit reads the grid back into sheets (name, headers,
// rows) and saves it as the next version, debounced, the same contract as the
// document editor (base_version, coalesced into one version while you type, a
// refused save keeps you here with the reason).
const AUTOSAVE_MS = 1500

export default class extends Controller {
    static targets = ["pane", "tab", "tabs", "addTab", "title", "status"]
    static values = { saveUrl: String, baseVersion: Number, panel: Boolean, viewUrl: String, panelUrl: String, documentTitle: String, icon: String }

    connect() {
        this._dirty = false
        this._rev = 0
        this._index = 0
        this._status("Saved")
    }

    disconnect() {
        clearTimeout(this._timer)
    }

    // --- editing ---

    changed() {
        this._dirty = true
        this._rev += 1
        this._status("Unsaved changes…")
        clearTimeout(this._timer)
        if (!this._blocked) this._timer = setTimeout(() => this._save(), AUTOSAVE_MS)
    }

    focused(event) {
        this._cell = event.currentTarget
    }

    // Enter moves down a row and Tab right, as in a spreadsheet; Shift+Tab goes back.
    key(event) {
        if (event.key !== "Enter" && event.key !== "Tab") return

        const cell = event.currentTarget
        const row = cell.parentElement
        const index = [...row.children].indexOf(cell)
        let target
        if (event.key === "Enter") {
            if (event.shiftKey) return
            const next = row.parentElement.tagName === "THEAD" ? row.parentElement.nextElementSibling?.firstElementChild : row.nextElementSibling
            target = next?.children[index]
        } else {
            target = event.shiftKey ? cell.previousElementSibling : cell.nextElementSibling
            if (target && !target.isContentEditable) target = null
        }
        if (!target?.isContentEditable) {
            // Past the last cell (Tab) or below the last row (Enter) is a new row, as in a spreadsheet.
            if (row.parentElement.tagName !== "TBODY" || event.shiftKey) return

            event.preventDefault()
            this._cell = cell
            this.addRow()
            if (event.key === "Enter") row.nextElementSibling.children[index]?.focus()
            return
        }

        event.preventDefault()
        target.focus()
    }

    // Pasting from Excel, Numbers or Sheets arrives as tab- and line-separated text.
    // One value goes into the cell as usual; a block fills cells from here, adding the
    // rows and columns it needs, rather than stuffing newlines into a single cell.
    paste(event) {
        const text = event.clipboardData?.getData("text/plain") ?? ""
        if (!/[\t\r\n]/.test(text.replace(/\r?\n$/, ""))) return

        event.preventDefault()
        const start = event.currentTarget
        const lines = text.replace(/\r\n?/g, "\n").replace(/\n$/, "").split("\n").map((line) => line.split("\t"))
        const pane = this._pane()
        const headerRow = pane.querySelector("thead tr")
        const columnAt = [...start.parentElement.children].indexOf(start)
        const inHeader = start.parentElement === headerRow
        const wanted = columnAt + Math.max(...lines.map((line) => line.length))
        while (headerRow.children.length < wanted) {
            const at = headerRow.children.length
            pane.querySelectorAll("tr").forEach((row) => row.append(this._editable(row.parentElement.tagName === "THEAD" ? "th" : "td", row === headerRow ? `Column ${at}` : "")))
        }
        let rows = [...pane.querySelectorAll("tbody tr")]
        let rowAt = inHeader ? -1 : rows.indexOf(start.parentElement)
        lines.forEach((values, i) => {
            const row = rowAt + i < 0 ? headerRow : this._rowAt(pane, rowAt + i)
            values.forEach((value, j) => { if (row.children[columnAt + j]?.isContentEditable) row.children[columnAt + j].textContent = value.trim() })
        })
        this._renumber()
        this.changed()
    }

    // The tbody row at this index, appended (blank) when the paste runs past the end.
    _rowAt(pane, index) {
        const body = pane.querySelector("tbody")
        const columns = pane.querySelectorAll("thead th[contenteditable]").length
        while (body.children.length <= index) {
            body.append(this._blankRow(columns, 0))
        }
        return body.children[index]
    }

    select(event) {
        if (event.currentTarget.isContentEditable) return

        this._show(Number(event.currentTarget.dataset.index))
    }

    // --- sheets ---

    addSheet() {
        if (this.paneTargets.length >= 10) return this._status("A spreadsheet holds up to 10 sheets.", true)

        const index = this.paneTargets.length
        const names = this.paneTargets.map((pane) => pane.dataset.sheetName.toLowerCase())
        let n = index + 1
        while (names.includes(`sheet${n}`)) n += 1
        const name = `Sheet${n}`

        const pane = document.createElement("div")
        pane.className = "document-grid-pane"
        pane.setAttribute("role", "tabpanel")
        pane.hidden = true
        pane.dataset.gridEditorTarget = "pane"
        pane.dataset.sheetName = name
        const scroll = document.createElement("div")
        scroll.className = "document-grid-scroll"
        const table = document.createElement("table")
        table.className = "document-grid-table"
        const head = table.createTHead().insertRow()
        const corner = document.createElement("th")
        corner.className = "document-grid-corner"
        corner.scope = "col"
        head.append(corner, ...["A", "B", "C"].map((letter) => this._editable("th", `Column ${letter}`)))
        const body = table.createTBody()
        for (let r = 1; r <= 5; r++) body.append(this._blankRow(3, r))
        scroll.append(table)
        pane.append(scroll)
        this.paneTargets.at(-1).after(pane)

        const tab = document.createElement("button")
        tab.type = "button"
        tab.className = "document-grid-tab"
        tab.setAttribute("role", "tab")
        tab.title = "Double-click to rename"
        tab.dataset.gridEditorTarget = "tab"
        tab.dataset.action = "grid-editor#select dblclick->grid-editor#rename"
        tab.textContent = name
        this.addTabTarget.before(tab)
        this._reindexTabs()
        this._show(index)
        this.changed()
    }

    // Double-click a tab to rename it; Enter or leaving it keeps the name, Escape drops the edit.
    rename(event) {
        const tab = event.currentTarget
        const index = this.tabTargets.indexOf(tab)
        const before = this.paneTargets[index].dataset.sheetName
        tab.contentEditable = "plaintext-only"
        tab.focus()
        document.getSelection()?.selectAllChildren(tab)
        const finish = (keep) => {
            tab.removeEventListener("blur", onBlur)
            tab.removeEventListener("keydown", onKey)
            tab.contentEditable = "false"
            const name = keep ? tab.textContent.replace(/[\[\]:*?/\\\n]/g, " ").replace(/\s+/g, " ").trim().slice(0, 31) : ""
            const taken = this.paneTargets.some((pane, i) => i !== index && pane.dataset.sheetName.toLowerCase() === name.toLowerCase())
            const final = name && !taken ? name : before
            tab.textContent = final
            if (final !== before) {
                this.paneTargets[index].dataset.sheetName = final
                this.changed()
            }
        }
        const onBlur = () => finish(true)
        const onKey = (e) => {
            if (e.key === "Enter") { e.preventDefault(); finish(true) }
            if (e.key === "Escape") { e.preventDefault(); finish(false) }
        }
        tab.addEventListener("blur", onBlur)
        tab.addEventListener("keydown", onKey)
    }

    deleteSheet() {
        if (this.paneTargets.length <= 1) return this._status("A spreadsheet needs at least one sheet.", true)
        if (!window.confirm(`Delete the sheet "${this._pane().dataset.sheetName}" and everything in it?`)) return

        this._pane().remove()
        this.tabTargets[this._index].remove()
        this._reindexTabs()
        this._show(Math.max(0, this._index - 1))
        this.changed()
    }

    _reindexTabs() {
        this.tabTargets.forEach((tab, i) => { tab.dataset.index = i })
    }

    _show(index) {
        this._index = index
        this.paneTargets.forEach((pane, i) => { pane.hidden = i !== index })
        this.tabTargets.forEach((tab, i) => {
            tab.classList.toggle("is-active", i === index)
            tab.setAttribute("aria-selected", String(i === index))
        })
        this._cell = null
    }

    _blankRow(columns, number) {
        const row = document.createElement("tr")
        row.append(this._rowNumber(number), ...Array.from({ length: columns }, () => this._editable("td")))
        return row
    }

    addRow() {
        const body = this._pane().querySelector("tbody")
        const columns = this._pane().querySelectorAll("thead th[contenteditable]").length
        const row = this._blankRow(columns, 0)
        const after = this._cell?.closest("tbody") ? this._cell.parentElement : null
        after ? after.after(row) : body.append(row)
        this._renumber()
        row.children[1]?.focus()
        this.changed()
    }

    deleteRow() {
        const row = this._cell?.closest("tbody") ? this._cell.parentElement : this._pane().querySelector("tbody tr:last-child")
        if (!row) return

        row.remove()
        this._cell = null
        this._renumber()
        this.changed()
    }

    addColumn() {
        const pane = this._pane()
        const at = this._cell ? [...this._cell.parentElement.children].indexOf(this._cell) + 1 : pane.querySelector("thead tr").children.length
        pane.querySelectorAll("tr").forEach((row) => {
            const header = row.parentElement.tagName === "THEAD"
            const cell = this._editable(header ? "th" : "td", header ? "New column" : "")
            row.children[at] ? row.children[at].before(cell) : row.append(cell)
        })
        pane.querySelector("thead tr").children[at]?.focus()
        this.changed()
    }

    deleteColumn() {
        const pane = this._pane()
        const headers = pane.querySelector("thead tr")
        if (headers.querySelectorAll("th[contenteditable]").length <= 1) return this._status("A sheet needs at least one column.", true)

        const at = this._cell ? [...this._cell.parentElement.children].indexOf(this._cell) : headers.children.length - 1
        if (at < 1) return

        pane.querySelectorAll("tr").forEach((row) => row.children[at]?.remove())
        this._cell = null
        this.changed()
    }

    async done() {
        clearTimeout(this._timer)
        if (this._dirty && !this._blocked) await this._save()
        if (this._dirty) return // a refused save keeps you here, with the reason showing

        this._leave()
    }

    // --- reading the grid back ---

    _pane() {
        return this.paneTargets[this._index]
    }

    _renumber() {
        this._pane().querySelectorAll("tbody tr").forEach((row, i) => { row.firstElementChild.textContent = i + 1 })
    }

    _rowNumber(n) {
        const th = document.createElement("th")
        th.className = "document-grid-rownum"
        th.scope = "row"
        th.textContent = n
        return th
    }

    _editable(tag, text = "") {
        const cell = document.createElement(tag)
        cell.setAttribute("contenteditable", "plaintext-only")
        cell.setAttribute("spellcheck", "false")
        cell.dataset.action = "input->grid-editor#changed focus->grid-editor#focused keydown->grid-editor#key paste->grid-editor#paste"
        cell.textContent = text
        return cell
    }

    _sheets() {
        return this.paneTargets.map((pane, i) => ({
            name: pane.dataset.sheetName || this.tabTargets[i]?.textContent.trim() || `Sheet${i + 1}`,
            headers: [...pane.querySelectorAll("thead th[contenteditable]")].map((th) => th.textContent.trim()),
            rows: [...pane.querySelectorAll("tbody tr")].map((row) => [...row.querySelectorAll("td")].map((td) => td.textContent.trim()))
        }))
    }

    // --- saving ---

    async _save() {
        if (this._saving) {
            this._again = true
            return
        }

        this._saving = true
        this._status("Saving…")
        const rev = this._rev
        try {
            const response = await fetch(this.saveUrlValue, {
                method: "PATCH",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this._csrf() },
                body: JSON.stringify({ title: this.titleTarget.value.trim(), sheets: this._sheets(), base_version: this.baseVersionValue, coalesce: true })
            })
            const data = await response.json().catch(() => ({}))
            if (response.ok) {
                this.baseVersionValue = data.version
                this._dirty = this._rev !== rev
                this._status(this._dirty ? "Unsaved changes…" : "Saved")
            } else {
                this._blocked = response.status === 409
                this._status(data.error || "That didn't save.", true)
            }
        } catch (_error) {
            this._status("Couldn't save - check your connection.", true)
        } finally {
            this._saving = false
            if (this._again && !this._blocked) {
                this._again = false
                this._save()
            }
        }
    }

    _leave() {
        if (this.panelValue) {
            window.dispatchEvent(new CustomEvent("jane:open-panel", {
                detail: { url: this.panelUrlValue, title: this.titleTarget.value.trim() || this.documentTitleValue, icon: this.iconValue || "bi-file-earmark-spreadsheet", label: "Spreadsheet" }
            }))
        } else {
            window.location.href = this.viewUrlValue
        }
    }

    _status(text, problem = false) {
        if (!this.hasStatusTarget) return

        this.statusTarget.textContent = text
        this.statusTarget.classList.toggle("is-problem", problem)
    }

    _csrf() {
        return document.querySelector("meta[name='csrf-token']")?.content || ""
    }
}
